import React, { useRef, useState } from 'react';
import apiService from '../services/ApiService';
import { IMAGE_UPLOAD_MAX_BYTES, IMAGE_UPLOAD_MAX_MB } from '../constants/uploadLimits';
import { ImageCropModal } from './ui/ImageCropModal';
import type { CropPresetId } from '../constants/imageCropPresets';
import { prepareImageFileForUpload } from '../utils/prepareImageFileForUpload';
import { hasNativeCapability, requestNativePhoto } from '../utils/nativeShell';

export interface ImageUploadProps {
  vehicleId: number;
  imageType: 'hero' | 'gallery' | 'modification';
  modificationId?: number;
  onImageUploaded: (imageData: any) => void;
  onError: (error: string) => void;
  maxFiles?: number;
  className?: string;
  onUploadComplete?: () => void;
  slotPosition?: number;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const FOCAL_PRESETS: Record<'hero' | 'gallery', CropPresetId> = {
  hero: 'vehicle_hero',
  gallery: 'vehicle_card',
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  vehicleId,
  imageType,
  modificationId,
  onImageUploaded,
  onError,
  maxFiles = 5,
  className = '',
  onUploadComplete,
  slotPosition,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingQueue, setPendingQueue] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/heic',
    'image/heif',
  ];
  const maxFileSize = IMAGE_UPLOAD_MAX_BYTES;
  const usesFocalCrop = imageType === 'hero' || imageType === 'gallery';
  const nativeCamera = hasNativeCapability('native_camera');

  const handleNativeCapture = async (source: 'camera' | 'library') => {
    if (uploading) return;
    setUploading(true);
    setUploadProgress([{ fileName: source === 'camera' ? 'Camera' : 'Library', progress: 40, status: 'uploading' }]);
    try {
      const result = await requestNativePhoto({
        purpose: imageType === 'modification' ? 'generic' : 'vehicle_gallery',
        vehicleId: imageType === 'modification' ? undefined : vehicleId,
        modificationId,
        source,
      });
      if (!result.ok) {
        if (result.error === 'cancelled') return;
        onError(result.error || 'Camera capture failed');
        return;
      }
      // Native uploaded gallery when vehicleId was set; otherwise hand URI back is not a File — refresh via callback shape.
      if (result.uploadId != null) {
        onImageUploaded({
          id: result.uploadId,
          url: result.uri,
          focal_x: result.focalX,
          focal_y: result.focalY,
        });
        onUploadComplete?.();
      } else if (result.uri) {
        // Local URI only (e.g. modification without upload path) — fall back to file picker message.
        onError('Use Choose photos for this image type, or attach from the library picker.');
      }
    } finally {
      setUploading(false);
      setUploadProgress([]);
    }
  };

  const validatePreparedFile = (file: File): string | null => {
    const typeOk =
      acceptedTypes.includes(file.type) ||
      !file.type ||
      file.type === 'image/jpeg' ||
      /\.jpe?g$/i.test(file.name);
    if (!typeOk && file.type && !file.type.startsWith('image/')) {
      return 'File type not supported. Please use JPEG, PNG, WebP, or GIF.';
    }
    if (file.size > maxFileSize) {
      return 'This photo is still too large after preparing. Try another image.';
    }
    return null;
  };

  const uploadOne = async (
    file: File,
    focal?: { x: number; y: number },
    index = 0
  ) => {
    setUploadProgress((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, progress: 50 } : item))
    );
    const result = await apiService.uploadVehicleImage(
      vehicleId,
      file,
      imageType,
      modificationId,
      slotPosition,
      undefined,
      focal
    );
    setUploadProgress((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, progress: 100, status: 'success' } : item
      )
    );
    onImageUploaded(result);
  };

  const runUploadBatch = async (files: File[]) => {
    setUploading(true);
    const progressArray: UploadProgress[] = files.map((file) => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const,
    }));
    setUploadProgress(progressArray);

    try {
      for (let i = 0; i < files.length; i++) {
        try {
          await uploadOne(files[i], undefined, i);
        } catch (error: any) {
          const errorMessage = error.message || 'Upload failed';
          setUploadProgress((prev) =>
            prev.map((item, idx) =>
              idx === i
                ? { ...item, progress: 0, status: 'error', error: errorMessage }
                : item
            )
          );
          onError(`${files[i].name}: ${errorMessage}`);
        }
      }
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress((current) => {
          const hasErrors = current.some((item) => item.status === 'error');
          if (!hasErrors) return [];
          setTimeout(() => setUploadProgress([]), 7000);
          return current;
        });
      }, 3000);
      if (onUploadComplete) {
        setUploadProgress((current) => {
          if (!current.some((item) => item.status === 'error')) {
            onUploadComplete();
          }
          return current;
        });
      }
    }
  };

  const startFocalQueue = (files: File[]) => {
    setPendingQueue(files.slice(1));
    setCropFile(files[0]);
    setCropOpen(true);
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    if (fileArray.length > maxFiles) {
      onError(`Maximum ${maxFiles} files allowed at once.`);
      return;
    }

    void (async () => {
      setUploading(true);
      const validFiles: File[] = [];
      const errors: string[] = [];
      for (const file of fileArray) {
        try {
          // Compress before size gate — iOS/iCloud originals are often >> 5MB until JPEG downscale.
          const prepared = await prepareImageFileForUpload(file, { maxLongEdge: 2048 });
          const error = validatePreparedFile(prepared);
          if (error) errors.push(`${file.name}: ${error}`);
          else validFiles.push(prepared);
        } catch {
          errors.push(`${file.name}: We couldn't prepare this photo. Try another image.`);
        }
      }
      setUploading(false);
      if (errors.length) onError(errors.join('\n'));
      if (!validFiles.length) return;

      if (usesFocalCrop) {
        startFocalQueue(validFiles);
      } else {
        void runUploadBatch(validFiles);
      }
    })();
  };

  const handleCropConfirm = async ({
    file,
    focal,
  }: {
    file: File;
    focal: { x: number; y: number };
  }) => {
    setUploading(true);
    setUploadProgress([{ fileName: file.name, progress: 0, status: 'uploading' }]);
    try {
      await uploadOne(file, focal, 0);
    } catch (error: any) {
      onError(`${file.name}: ${error.message || 'Upload failed'}`);
    } finally {
      setUploading(false);
      setCropOpen(false);
      setCropFile(null);
      if (pendingQueue.length > 0) {
        const [next, ...rest] = pendingQueue;
        setPendingQueue(rest);
        setCropFile(next);
        setCropOpen(true);
      } else if (onUploadComplete) {
        onUploadComplete();
      }
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {nativeCamera ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={uploading}
            onClick={() => void handleNativeCapture('camera')}
            className="btn-primary btn-sm min-h-[44px] px-4 disabled:opacity-50"
          >
            Take photo
          </button>
          <button
            type="button"
            disabled={uploading}
            onClick={() => void handleNativeCapture('library')}
            className="btn-secondary btn-sm min-h-[44px] px-4 disabled:opacity-50"
          >
            Choose from library
          </button>
        </div>
      ) : null}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFileSelect(e.dataTransfer.files);
        }}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${dragOver ? 'border-orange-500/60 bg-orange-500/10' : 'border-white/[0.10] hover:border-white/[0.18] bg-[#141414]/50'}
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={acceptedTypes.join(',')}
          onChange={(e) => {
            handleFileSelect(e.target.files);
            e.target.value = '';
          }}
          className="hidden"
          disabled={uploading}
        />

        <div className="space-y-4">
          <div className="flex justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="text-lg font-medium text-white mb-2">
              {uploading
                ? uploadProgress.length > 0
                  ? 'Uploading…'
                  : 'Preparing image…'
                : 'Upload Images'}
            </p>
            <p className="text-sm text-gray-400 mb-2">
              {nativeCamera
                ? 'Or drag and drop files here / tap to use the web file picker'
                : `Drag and drop ${maxFiles > 1 ? `up to ${maxFiles} ` : ''}image${maxFiles > 1 ? 's' : ''} here, or click to select`}
            </p>
            <p className="text-xs text-gray-500">
              Supports: JPEG, PNG, WebP, GIF · Max size: {IMAGE_UPLOAD_MAX_MB}MB each
            </p>
          </div>
          {usesFocalCrop ? (
            <div className="bg-orange-950/30 border border-orange-800/40 rounded-lg p-3 text-left">
              <p className="text-sm text-orange-200/90">
                You will set the photo focus for each image so cards and the gallery crop look right.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {uploadProgress.length > 0 ? (
        <div className="space-y-2">
          {uploadProgress.map((item, index) => (
            <div key={index} className="bg-[#141414] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white truncate flex-1 mr-2">{item.fileName}</span>
                <span className="text-xs text-gray-400">
                  {item.status === 'uploading' && `${item.progress}%`}
                  {item.status === 'success' && <span className="text-emerald-400">Success</span>}
                  {item.status === 'error' && <span className="text-red-400">Failed</span>}
                </span>
              </div>
              {item.status === 'uploading' ? (
                <div className="w-full bg-[#161616] rounded-full h-2">
                  <div className="bg-orange-600 h-2 rounded-full transition-all" style={{ width: `${item.progress}%` }} />
                </div>
              ) : null}
              {item.status === 'error' && item.error ? (
                <p className="text-xs text-red-400 mt-1">{item.error}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <ImageCropModal
        isOpen={cropOpen}
        onClose={() => {
          setCropOpen(false);
          setCropFile(null);
          setPendingQueue([]);
        }}
        file={cropFile}
        presetId={FOCAL_PRESETS[imageType as 'hero' | 'gallery']}
        onConfirm={handleCropConfirm}
        busy={uploading}
      />
    </div>
  );
};

export { ImageUpload };
